import React from 'react';

import { motion } from 'framer-motion';

import LogoOnlySvg from '../assets/loanguard_logo_only.svg';

interface LogoProps {
  /**
   * Whether to show only the logo icon without text
   */
  collapsed?: boolean;
  /**
   * Whether to use white text color for the logo text
   */
  whiteText?: boolean;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * Optional additional className for the wrapper
   */
  className?: string;
  /**
   * Optional size for the logo icon
   */
  iconSize?: 'sm' | 'md' | 'lg';
  /**
   * Whether to use motion animation
   */
  animate?: boolean;
  /**
   * Whether to show abbreviated text (TDFA) instead of full association name
   */
  abbreviated?: boolean;
}

/**
 * Logo component for consistent branding across the application
 */
export const Logo: React.FC<LogoProps> = ({
  collapsed = false,
  whiteText = false,
  onClick,
  className = '',
  iconSize = 'md',
  animate = false,
  abbreviated = false,
}) => {
  // Define icon size classes
  const iconSizeClasses = {
    sm: 'h-10 w-10',
    md: 'h-14 w-14',
    lg: 'h-18 w-18',
  };

  // Define text color classes
  const textColorClass = whiteText ? 'text-white' : 'text-[#18216d]';

  // Define base component
  const LogoContent = (
    <div
      role="button"
      tabIndex={onClick ? 0 : -1}
      onClick={onClick}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      className={`flex items-center ${onClick ? 'cursor-pointer' : ''} ${className}`}
    >
      <img
        src={LogoOnlySvg}
        alt="TDFA Logo"
        className={`${iconSizeClasses[iconSize]} mr-4 object-contain`}
      />

      {!collapsed && (
        <div>
          {abbreviated ? (
            <h1
              className={`${textColorClass} text-2xl md:text-3xl font-bold tracking-wide`}
            >
              TDFA
            </h1>
          ) : (
            <>
              <h1
                className={`${textColorClass} text-xl md:text-2xl font-bold leading-tight`}
              >
                Tirupur District
              </h1>
              <h2
                className={`${textColorClass} text-lg md:text-xl font-semibold leading-tight`}
              >
                Financiers Association
              </h2>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Return with or without animation
  if (animate) {
    return (
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8, type: 'spring' }}
      >
        {LogoContent}
      </motion.div>
    );
  }

  return LogoContent;
};

export default Logo;
